import React, { useState, useRef, useEffect } from "react";

interface ImageMaskSelectorProps {
  imageUrl?: string;
  onMaskGenerated: (
    maskDataUrl: string,
    coordinates: SelectionCoordinates
  ) => void;
  onRegionsUpdated?: (regions: any[]) => void;
}

export interface SelectionCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ClickableRegion extends SelectionCoordinates {
  file?: File;
  id: string;
}

const ImageMaskSelector: React.FC<ImageMaskSelectorProps> = ({
  imageUrl,
  onMaskGenerated,
  onRegionsUpdated,
}) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selection, setSelection] = useState<SelectionCoordinates | null>(null);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // For manual coordinate input
  const [manualX, setManualX] = useState<string>("");
  const [manualY, setManualY] = useState<string>("");
  const [manualWidth, setManualWidth] = useState<string>("");
  const [manualHeight, setManualHeight] = useState<string>("");

  // For clickable regions
  const [clickableRegions, setClickableRegions] = useState<ClickableRegion[]>(
    []
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeRegionId = useRef<string | null>(null);

  // For tracking previous regions to avoid unnecessary updates
  const prevRegionsRef = useRef<string>("");

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const img = new Image();
        img.onload = () => {
          setImage(img);
          setSelection(null);
          setStartPoint(null);
          // Reset manual inputs when loading a new image
          setManualX("");
          setManualY("");
          setManualWidth("");
          setManualHeight("");
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  // Add effect to handle imageUrl prop
  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setSelection(null);
        setStartPoint(null);
        // Reset manual inputs when loading a new image
        setManualX("");
        setManualY("");
        setManualWidth("");
        setManualHeight("");
      };
      img.src = imageUrl;
    }
  }, [imageUrl]);

  // Draw crosshair at the specified coordinates
  const drawCrosshair = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    size: number = 10
  ) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([]);

    // Horizontal line
    ctx.beginPath();
    ctx.moveTo(x - size, y);
    ctx.lineTo(x + size, y);
    ctx.stroke();

    // Vertical line
    ctx.beginPath();
    ctx.moveTo(x, y - size);
    ctx.lineTo(x, y + size);
    ctx.stroke();

    // Draw a small circle at the center
    ctx.beginPath();
    ctx.arc(x, y, 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  };

  // Calculate end point of the selection
  const getEndPoint = (
    selection: SelectionCoordinates,
    startPoint: { x: number; y: number }
  ) => {
    return {
      x:
        selection.x + selection.width === startPoint.x
          ? selection.x
          : selection.x + selection.width,
      y:
        selection.y + selection.height === startPoint.y
          ? selection.y
          : selection.y + selection.height,
    };
  };

  // Update canvas when image or selection changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions to match image
    canvas.width = image.width;
    canvas.height = image.height;

    // Draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);

    // Draw clickable regions
    clickableRegions.forEach((region) => {
      // Set cursor to pointer when hovering over regions
      if (
        mousePosition &&
        mousePosition.x >= region.x &&
        mousePosition.x <= region.x + region.width &&
        mousePosition.y >= region.y &&
        mousePosition.y <= region.y + region.height
      ) {
        canvas.style.cursor = "pointer";
      }

      ctx.fillStyle = region.file
        ? "oklch(0.558 0.288 302.321 / 0.3)" // Purple if has file
        : "oklch(0.558 0.288 302.321 / 0.3)"; // Orange if no file
      ctx.fillRect(region.x, region.y, region.width, region.height);

      // Draw border
      ctx.strokeStyle = "oklch(0.558 0.288 302.321 / 0.3)";
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.strokeRect(region.x, region.y, region.width, region.height);

      // Draw label with region ID
      ctx.font = "12px Arial";
      ctx.fillStyle = "white";
      ctx.fillRect(region.x, region.y - 20, 100, 20);
      ctx.fillStyle = "black";
      ctx.fillText(
        `Region ${region.id.slice(0, 5)}${region.file ? " ✓" : ""}`,
        region.x + 5,
        region.y - 5
      );
    });

    // Reset cursor to default if not hovering over any region
    if (mousePosition) {
      let isOverRegion = false;
      for (const region of clickableRegions) {
        if (
          mousePosition.x >= region.x &&
          mousePosition.x <= region.x + region.width &&
          mousePosition.y >= region.y &&
          mousePosition.y <= region.y + region.height
        ) {
          isOverRegion = true;
          break;
        }
      }
      if (!isOverRegion) {
        canvas.style.cursor = "default";
      }
    }

    // Draw current mouse position crosshair if we're hovering over the canvas
    if (mousePosition && !isDragging) {
      drawCrosshair(ctx, mousePosition.x, mousePosition.y, "#ff9800");
    }

    // Draw selection rectangle if available
    if (selection) {
      // Draw selection outline
      ctx.strokeStyle = "#00a8ff";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        selection.x,
        selection.y,
        selection.width,
        selection.height
      );

      // Add semi-transparent overlay to highlight selection
      ctx.fillStyle = "rgba(0, 168, 255, 0.2)";
      ctx.fillRect(selection.x, selection.y, selection.width, selection.height);

      // Draw start point if available
      if (startPoint) {
        drawCrosshair(ctx, startPoint.x, startPoint.y, "#4CAF50", 15); // Green for start

        // Draw end point (opposite corner)
        const endPoint = getEndPoint(selection, startPoint);
        drawCrosshair(ctx, endPoint.x, endPoint.y, "#F44336", 15); // Red for end
      }
    }
  }, [
    image,
    selection,
    startPoint,
    mousePosition,
    isDragging,
    clickableRegions,
  ]);

  // Call the onRegionsUpdated callback when regions actually change
  useEffect(() => {
    if (!onRegionsUpdated) return;

    // Only update if regions have actually changed
    const regionsJSON = JSON.stringify(clickableRegions);
    if (regionsJSON !== prevRegionsRef.current) {
      prevRegionsRef.current = regionsJSON;
      onRegionsUpdated(clickableRegions);
    }
  }, [clickableRegions, onRegionsUpdated]);

  // Mouse events for selection
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!image) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) * (canvas.width / rect.width));
    const y = Math.floor(
      (e.clientY - rect.top) * (canvas.height / rect.height)
    );

    // Check if click is inside any clickable region
    const clickedRegion = clickableRegions.find(
      (region) =>
        x >= region.x &&
        x <= region.x + region.width &&
        y >= region.y &&
        y <= region.y + region.height
    );

    if (clickedRegion) {
      // Store the active region ID and trigger file input click
      activeRegionId.current = clickedRegion.id;
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    setIsDragging(true);
    setStartPoint({ x, y });
    setSelection({
      x,
      y,
      width: 0,
      height: 0,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!image) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) * (canvas.width / rect.width));
    const y = Math.floor(
      (e.clientY - rect.top) * (canvas.height / rect.height)
    );

    // Update the mouse position for crosshair
    setMousePosition({ x, y });

    if (!isDragging || !startPoint) return;

    // Calculate new selection
    const width = x - startPoint.x;
    const height = y - startPoint.y;

    setSelection({
      x: width >= 0 ? startPoint.x : x,
      y: height >= 0 ? startPoint.y : y,
      width: Math.abs(width),
      height: Math.abs(height),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);

    // Log the coordinates of the selection
    if (selection) {
      console.log("Selection Coordinates:", selection);

      // Update manual input values
      setManualX(selection.x.toString());
      setManualY(selection.y.toString());
      setManualWidth(selection.width.toString());
      setManualHeight(selection.height.toString());
    }
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setMousePosition(null);
  };

  // Handle file selection for a region
  const handleRegionFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && activeRegionId.current) {
      setClickableRegions((regions) =>
        regions.map((region) =>
          region.id === activeRegionId.current ? { ...region, file } : region
        )
      );
      console.log(
        `File '${file.name}' assigned to region ${activeRegionId.current}`
      );
    }

    // Reset active region
    activeRegionId.current = null;

    // Reset file input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle applying manual coordinates
  const handleApplyCoordinates = () => {
    if (!image) return;

    // Parse and validate coordinates
    const x = parseInt(manualX);
    const y = parseInt(manualY);
    const width = parseInt(manualWidth);
    const height = parseInt(manualHeight);

    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
      alert("Please enter valid numbers for all coordinates");
      return;
    }

    if (width <= 0 || height <= 0) {
      alert("Width and height must be greater than 0");
      return;
    }

    // Apply the selection
    setSelection({ x, y, width, height });
  };

  // Add the current selection as a clickable region
  const addClickableRegion = () => {
    if (!selection) return;

    const newRegion: ClickableRegion = {
      ...selection,
      id: Math.random().toString(36).substring(2, 9), // Generate a unique ID
    };

    setClickableRegions([...clickableRegions, newRegion]);
    console.log("Added new clickable region:", newRegion);
  };

  // Clear all clickable regions
  const clearClickableRegions = () => {
    setClickableRegions([]);
  };

  // Generate and save binary mask
  const handleCreateMask = () => {
    if (!image || !selection) return;

    // Create a new canvas for the mask
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = image.width;
    maskCanvas.height = image.height;
    const ctx = maskCanvas.getContext("2d");

    if (!ctx) return;

    // Fill with black background
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

    // Draw white shape for the selected area
    ctx.fillStyle = "white";
    ctx.fillRect(selection.x, selection.y, selection.width, selection.height);

    // Create image from canvas
    const dataURL = maskCanvas.toDataURL("image/png");

    // Call the callback first
    if (onMaskGenerated) {
      onMaskGenerated(dataURL, selection);
    }

    // // Then trigger download
    // const link = document.createElement("a");
    // link.download = "binary-mask.png";
    // link.href = dataURL;
    // link.click();
  };

  const handleResetSelection = () => {
    setSelection(null);
    setStartPoint(null);
    setManualX("");
    setManualY("");
    setManualWidth("");
    setManualHeight("");
  };

  return (
    <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg shadow mb-6">
      <h2 className="text-xl font-semibold mb-4">Create Image Mask</h2>

      {/* Only show upload input if no imageUrl is provided */}
      {!imageUrl && (
        <div className="flex flex-col mb-4 w-full">
          <label className="mb-2 font-medium">Upload Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="border p-2 rounded"
          />
        </div>
      )}

      <div
        className="relative border-2 border-gray-300 rounded bg-gray-200 my-4 max-w-full"
        style={{
          height: image ? "auto" : "300px",
          width: "100%",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{
            maxWidth: "100%",
            maxHeight: "500px",
            objectFit: "contain",
            cursor: "crosshair",
          }}
        />
        {!image && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500">
            Upload an image to start
          </div>
        )}

        {/* Hidden file input for region file selection */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleRegionFileSelect}
          accept="image/*"
        />
      </div>

      {/* Manual coordinate inputs */}
      {image && (
        <div className="w-full mb-4 bg-gray-100 p-3 rounded-lg">
          <h3 className="font-medium mb-2">Manual Coordinates:</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-sm text-gray-600">X:</label>
              <input
                type="number"
                value={manualX}
                onChange={(e) => setManualX(e.target.value)}
                className="w-full p-1 border rounded"
                min="0"
                max={image ? image.width : undefined}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600">Y:</label>
              <input
                type="number"
                value={manualY}
                onChange={(e) => setManualY(e.target.value)}
                className="w-full p-1 border rounded"
                min="0"
                max={image ? image.height : undefined}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600">Width:</label>
              <input
                type="number"
                value={manualWidth}
                onChange={(e) => setManualWidth(e.target.value)}
                className="w-full p-1 border rounded"
                min="1"
                max={image ? image.width : undefined}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600">Height:</label>
              <input
                type="number"
                value={manualHeight}
                onChange={(e) => setManualHeight(e.target.value)}
                className="w-full p-1 border rounded"
                min="1"
                max={image ? image.height : undefined}
              />
            </div>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleApplyCoordinates}
              className="bg-blue-500 text-white px-3 py-1 text-sm rounded hover:bg-blue-600"
            >
              Apply Coordinates
            </button>
            <button
              onClick={addClickableRegion}
              disabled={!selection}
              className={`px-3 py-1 text-sm rounded ${
                !selection
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-orange-500 text-white hover:bg-orange-600"
              }`}
            >
              Add as Clickable Region
            </button>
            {clickableRegions.length > 0 && (
              <button
                onClick={clearClickableRegions}
                className="bg-red-500 text-white px-3 py-1 text-sm rounded hover:bg-red-600"
              >
                Clear All Regions
              </button>
            )}
          </div>
        </div>
      )}

      {selection && (
        <div className="mt-2 mb-4 text-sm bg-blue-50 p-2 rounded w-full">
          <p className="font-medium">Selection Details:</p>
          <p>
            Position: X={selection.x}, Y={selection.y}
          </p>
          <p>
            Size: Width={selection.width}, Height={selection.height}
          </p>
        </div>
      )}

      {clickableRegions.length > 0 && (
        <div className="mt-2 mb-4 text-sm bg-orange-50 p-2 rounded w-full">
          <p className="font-medium">
            Clickable Regions: {clickableRegions.length}
          </p>
          <div className="max-h-40 overflow-y-auto">
            <ul className="divide-y divide-orange-100">
              {clickableRegions.map((region) => (
                <li key={region.id} className="py-1">
                  Region {region.id.slice(0, 5)} - X:{region.x}, Y:{region.y},
                  W:{region.width}, H:{region.height}
                  {region.file && (
                    <span className="ml-2 text-green-600">
                      ✓ {region.file.name.slice(0, 15)}
                      {region.file.name.length > 15 ? "..." : ""}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="flex gap-3 mt-2">
        <button
          onClick={handleCreateMask}
          disabled={!image || !selection}
          className={`px-4 py-2 rounded ${
            !image || !selection
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          Create & Download Mask
        </button>

        <button
          onClick={handleResetSelection}
          disabled={!selection}
          className={`px-4 py-2 rounded ${
            !selection
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-yellow-500 text-white hover:bg-yellow-600"
          }`}
        >
          Reset Selection
        </button>
      </div>

      <div className="mt-4 text-gray-600 text-sm w-full">
        <p className="font-medium">Instructions:</p>
        <ul className="list-disc list-inside">
          <li>Upload an image first</li>
          <li>
            Click and drag to create a selection or use manual coordinates
          </li>
          <li>
            The <span className="text-green-600">green</span> crosshair shows
            the start point
          </li>
          <li>
            The <span className="text-red-600">red</span> crosshair shows the
            end point
          </li>
          <li>Click "Create & Download Mask" to generate a binary mask</li>
          <li>
            Click "Add as Clickable Region" to create an{" "}
            <span className="text-orange-600">orange</span> clickable area
          </li>
          <li>
            Click on an orange region to assign an image to it (turns{" "}
            <span className="text-green-600">green</span> when assigned)
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ImageMaskSelector;
